/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 * - js-cookie (v3.0.5): https://github.com/js-cookie/js-cookie
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Country/region selector.
 *
 * The list of countries, the active country and its currency are all rendered server side from the
 * store's active markets (`localization.available_countries` / `localization.country`), so this
 * element only has to deal with behaviour:
 *
 * - keep the modal's "you are shopping in..." summary in sync with the pending selection,
 * - offer the visitor's geolocated country the first time they land on the site.
 *
 * Submitting is left to the native `{% form 'localization' %}`, which Shopify redirects back to the
 * current page.
 */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector("#country-modal-select");
    this.modal = document.getElementById("country-modal");
    this.detectedCode = null;

    if (!this.querySelector("form") || !this.select) {
      console.error(
        "<localization-form> expects a localization form containing a #country-modal-select element."
      );
      return;
    }

    // Rendered as "selected" by Liquid, so this is whatever market the visitor is currently browsing.
    this.currentCode = this.select.value;

    this.controlledElements = (this.select.getAttribute("aria-controls") || "")
      .split(",")
      .map((id) => document.getElementById(id.trim()))
      .filter(Boolean);

    this.select.addEventListener("change", () => this.updateControlledElements());

    // "Change country/region" pre-selects the geolocated country when we have one.
    this.querySelector("#country-modal-change")?.addEventListener("click", () => {
      if (this.detectedCode && this.detectedCode !== this.select.value) {
        this.selectCountry(this.detectedCode);
      }
    });

    // "Cancel" puts the summary back to the country actually being browsed.
    this.querySelector("#country-modal-cancel")?.addEventListener("click", () => {
      this.selectCountry(this.currentCode);
    });

    this.suggestDetectedCountry();
  }

  get options() {
    return Array.from(this.select.options);
  }

  selectCountry(isoCode) {
    this.select.value = isoCode;
    this.select.dispatchEvent(new Event("change"));
  }

  /**
   * Mirrors the pending selection into the elements the select declares as `aria-controls`, so the
   * modal summary reads back the country the visitor is about to switch to.
   */
  updateControlledElements() {
    const option = this.select.selectedOptions[0];

    if (!option) {
      return;
    }

    const name = option.textContent.trim();
    const currency = option.dataset.currency || "";

    this.controlledElements.forEach((element) => {
      const elementsWithSlots = Array.from(element.querySelectorAll("[slot]"));

      if (!elementsWithSlots.length) {
        element.textContent = name;
        return;
      }

      elementsWithSlots.forEach((elementWithSlot) => {
        switch (elementWithSlot.getAttribute("slot")) {
          case "selected-country":
            elementWithSlot.textContent = `${name} (${currency})`;
            break;
          case "selected-country-name":
            elementWithSlot.textContent = name;
            break;
          case "selected-country-currency":
            elementWithSlot.textContent = currency;
            break;
        }
      });
    });
  }

  /**
   * Opens the modal once per session when the visitor appears to be browsing from a country we sell
   * to, but which is not the one they are currently shopping in. Countries outside of the active
   * markets are simply left alone: there is nothing to offer them.
   */
  suggestDetectedCountry() {
    if (!this.modal || Cookies.get("geolocationFetched")) {
      return;
    }

    fetch("/browsing_context_suggestions.json")
      .then((response) => response.json())
      .then((suggestions) => {
        Cookies.set("geolocationFetched", "true");

        const requestedCode = new URLSearchParams(window.location.search).get("country");
        const detectedCode = (
          requestedCode ||
          suggestions?.detected_values?.country?.handle ||
          ""
        ).toUpperCase();

        if (!detectedCode || detectedCode === this.currentCode) {
          return;
        }

        if (!this.options.some((option) => option.value === detectedCode)) {
          return;
        }

        this.detectedCode = detectedCode;
        this.modal.open = true;
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

window.addEventListener("DOMContentLoaded", () => {
  window.customElements.define("localization-form", LocalizationForm);
});
